import { rewardIcons } from "@/components/icons/rewardIcons";
import { PRIZE_ROTATION_MS, prizes, wheelRewards } from "@/content/site";
import { useCarousel } from "@/hooks/useCarousel";
import { asset } from "@/lib/assets";
import { cn } from "@/lib/cn";

export function Prizes() {
  const { index, goTo } = useCarousel(prizes.length, PRIZE_ROTATION_MS);
  const activePrize = prizes[index];

  return (
    <section id="premios" className="scroll-mt-20 bg-brand-navy py-16 sm:py-20 lg:scroll-mt-28 lg:py-24">
      <div className="container mx-auto px-5">
        <h2 className="text-center text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold uppercase leading-none text-white">
          Prémios <span className="italic text-brand-sky">roda da sorte</span>
        </h2>

        <div className="mt-12 grid gap-10 md:grid-cols-2 md:items-center lg:mt-16 lg:gap-16">
          {/* Prize sits on a solid sky panel and breaks past its edges. */}
          <figure className="m-0">
            {/* Sized against the column, not the viewport, so the ring can
                never overlap the rewards beside it. */}
            <div className="relative mx-auto aspect-square w-full max-w-96 rounded-full border-4 border-brand-sky bg-brand-sky/10">
              {activePrize && (
                <img
                  className="absolute left-1/2 top-1/2 w-[104%] max-w-none -translate-x-1/2 -translate-y-1/2 lg:w-[112%]"
                  src={asset(activePrize.image)}
                  alt={activePrize.name}
                  loading="lazy"
                  decoding="async"
                />
              )}
            </div>

            <figcaption className="mt-8 text-center">
              <span className="font-roboto text-xs uppercase tracking-[0.25em] text-brand-sky">Prémio principal</span>
              <span className="mt-3 block text-[clamp(1.5rem,3.5vw,2.75rem)] font-extrabold uppercase italic leading-none text-white">
                {activePrize?.name}
              </span>
            </figcaption>

            {prizes.length > 1 && (
              <div className="mt-6 flex justify-center gap-3">
                {prizes.map((prize, prizeIndex) => (
                  <button
                    key={prize.id}
                    type="button"
                    aria-label={`Ver ${prize.name}`}
                    aria-current={prizeIndex === index}
                    onClick={() => {
                      goTo(prizeIndex);
                    }}
                    className={cn(
                      "h-3 rounded-full transition-all duration-300",
                      prizeIndex === index ? "w-10 bg-brand-sky" : "w-3 bg-white/30 hover:bg-white/60",
                    )}
                  />
                ))}
              </div>
            )}
          </figure>

          <div className="text-white">
            <span className="font-roboto text-xs uppercase tracking-[0.25em] text-brand-sky">O que podes ganhar</span>

            <p className="mt-3 text-2xl font-bold lg:text-3xl">Gira a roda e leva um destes prémios</p>

            {/* Horizontal cards keep the icon and label on one reading line. */}
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {wheelRewards.map((reward) => {
                const Icon = rewardIcons[reward.id];

                return (
                  <li
                    key={reward.id}
                    className={cn(
                      "flex items-center gap-4 rounded-2xl p-4 lg:p-5",
                      reward.wide ? "bg-brand-sky text-brand-navy sm:col-span-2" : "bg-brand-deep text-white",
                    )}
                  >
                    <span
                      className={cn(
                        "flex aspect-square w-11 shrink-0 items-center justify-center rounded-full p-2.5 lg:w-12",
                        reward.wide ? "bg-brand-navy/15" : "bg-brand-sky",
                      )}
                    >
                      {/* Without an explicit size the SVG uses its default
                          replaced-element box and overflows the badge. */}
                      {Icon ? <Icon className="h-full w-full" /> : null}
                    </span>
                    <span className="font-bold lg:text-lg xl:text-xl">{reward.label}</span>
                  </li>
                );
              })}
            </ul>

            <p className="mt-8 border-l-4 border-brand-sky pl-5 text-lg/relaxed font-medium text-white/70 lg:text-xl">
              Procura a <span className="font-bold text-white">Roda da sorte</span> no interior do{" "}
              <span className="font-bold text-white">Casino</span>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
